/** R12: the responsive fallback is measured, not guessed.
 *
 *  "If the widest visible calendar title or rapid-log line **would clip** at the
 *  chosen column count, drop one column. Legibility outranks day count at every
 *  width."
 *
 *  A media query cannot express that, because it does not know what the day
 *  holds. This measures the content against the column the breakpoint chose and
 *  overrides the breakpoint when the content will not fit.
 *
 *  Text is measured on the mono grid rather than with `measureText`: the whole
 *  design is column-aligned monospace, so a character count times an advance
 *  width is both accurate and cheap, and it needs no canvas, no layout pass and
 *  no font to have loaded.
 */

import { columnWidth, ritualFor, type Layout } from './breakpoints';

/** Advance width of one monospace character at the given font size. JetBrains
 *  Mono is 0.6 em per glyph, which is the same ratio the terminal client's
 *  column arithmetic assumes. */
export const MONO_ADVANCE = 0.6;

export function textWidth(text: string, fontSize: number): number {
  return text.length * fontSize * MONO_ADVANCE;
}

/** How many characters fit in `available` pixels. */
export function charsThatFit(available: number, fontSize: number): number {
  return Math.max(0, Math.floor(available / (fontSize * MONO_ADVANCE)));
}

/** Lines this text needs in `available` pixels, wrapping the way a browser does.
 *
 *  Greedy, at word boundaries — and that distinction is the whole point. Dividing
 *  the whole string's width by the available width models *character* wrapping,
 *  which no browser does: `BuJo planning day` at 14px in 80px of lane is 143px of
 *  glyphs, so the division says two lines, while the browser breaks at the spaces
 *  and takes three (`BuJo` / `planning` / `day`). R12 measured with the division
 *  and therefore never fired on exactly the case it exists for.
 *
 *  A word wider than the line counts for as many lines as it needs. It will
 *  either break or overflow the lane depending on `overflow-wrap`, and overflow
 *  is worse than wrapping — so counting it as a single line would make R12 blind
 *  to the one case it should react to most strongly.
 */
export function linesNeeded(text: string, available: number, fontSize: number): number {
  if (available <= 0) return Number.POSITIVE_INFINITY;
  const space = textWidth(' ', fontSize);
  let lines = 0;
  let used = 0;
  for (const word of text.split(/\s+/).filter(Boolean)) {
    const width = textWidth(word, fontSize);
    if (lines === 0) lines = 1;
    const extended = used === 0 ? width : used + space + width;
    if (extended <= available) {
      used = extended;
      continue;
    }
    // Only move to a new line if the current one already holds something.
    if (used > 0) lines += 1;
    if (width <= available) {
      used = width;
    } else {
      lines += Math.ceil(width / available) - 1;
      // Nothing more fits beside it.
      used = available;
    }
  }
  return Math.max(1, lines);
}

/** The calendar title's budget, from the truncation table: `never truncates ·
 *  wraps to 2 lines`. Past two it would clip, and that is the condition R12
 *  drops a column to avoid. */
export const CALENDAR_TITLE_LINES = 2;

/** The schedule row's own numbers, so the measurement matches what renders.
 *
 *  From `components/calendar/EventRow.jsx`: the title is `--bujo-fs-event`, and
 *  everything before it is fixed width — the time column, the calendar chip, and
 *  the two `--bujo-space-3` gaps between them. Measuring against a lane without
 *  subtracting those would think a title has half again as much room as it does.
 */
export const SCHEDULE_TITLE_SIZE = 14;
export const SCHEDULE_ROW_RESERVED = 52 + 12 + 18 + 12;

/** How a measured drop names itself. A wide screen showing one day is
 *  indistinguishable from a layout bug unless the layout says why. */
export function droppedBadge(before: Layout, after: Layout): string {
  return after.days < before.days
    ? 'ONE DAY · TITLES WOULD CLIP'
    : 'ONE LANE · TITLES WOULD CLIP';
}

export interface Measured {
  layout: Layout;
  /** True when the breakpoint's column count was reduced because content would
   *  have clipped. Surfaced in the UI, because a silently different layout is
   *  indistinguishable from a bug. */
  dropped: boolean;
}

/** Apply R12 to a chosen layout.
 *
 *  **What is measured, and what deliberately is not.** The truncation table says
 *  a rapid-log entry `never truncates · wraps to any height`, so its length can
 *  never force a column drop — wrapping already handles it, and measuring it
 *  would drop days for no reason. A calendar title is the binding case: it
 *  `wraps to 2 lines` and clips past that, so it is the thing that decides
 *  whether a column count is legible.
 *
 *  `reserved` is the non-text width in a schedule row: the time column, the
 *  monogram chip and their gaps.
 */
export function fitLayout(
  width: number,
  chosen: Layout,
  longestCalendarTitle: string,
  fontSize: number,
  reserved: number,
): Measured {
  // One day and one lane is the floor; there is nothing left to drop.
  if (chosen.days === 1 && chosen.lanes === 1) return { layout: chosen, dropped: false };
  if (!longestCalendarTitle) return { layout: chosen, dropped: false };

  const fits = (candidate: Layout) => {
    const column = columnWidth(width, candidate);
    // Two lanes split the column: the schedule takes the smaller share, and a
    // calendar title has to live in that.
    const lane = candidate.lanes === 2 ? Math.floor(column * 0.42) : column;
    return (
      linesNeeded(longestCalendarTitle, lane - reserved, fontSize) <= CALENDAR_TITLE_LINES
    );
  };

  if (fits(chosen)) return { layout: chosen, dropped: false };

  // Drop days before lanes: at a width that earned two lanes, the
  // schedule-beside-the-log split is the point of the layout, and one day of it
  // is worth more than two cramped days.
  const fallbacks: Layout[] =
    chosen.days === 2 && chosen.lanes === 2
      ? [
          { ...chosen, days: 1, ritual: ritualFor(1, 2) },
          { ...chosen, days: 1, lanes: 1, ritual: 'in-column' },
        ]
      : chosen.days === 2
        ? [{ ...chosen, days: 1, ritual: 'in-column' }]
        : [{ ...chosen, lanes: 1, ritual: 'in-column' }];

  for (const candidate of fallbacks) {
    if (fits(candidate)) return { layout: candidate, dropped: true };
  }
  const last = fallbacks[fallbacks.length - 1] ?? chosen;
  return { layout: last, dropped: true };
}
